import { Spin } from 'antd';
import { ReactElement, useMemo } from 'react';
import { Route, Navigate, Routes as RRDRoutes, Outlet } from 'react-router-dom';

import { useAuthSession, useSectionAccess } from './hooks';
import { AccountSecurity } from './pages/private/accountSecurity/index';
import UnauthorizedPage from './pages/errors/unauthorized/unauthorizedPage';
import { ChangeYourPassword } from './pages/public/changeYourPassword/index';
import { ConfirmFinancialOfficers } from './pages/public/confirmFinancialOfficers/ConfirmFinancialOfficers';
import ForgotMyPassword from './pages/public/forgotMyPassword/index';
import SignIn from './pages/public/signIn/index';
import { Verify2FA } from './pages/public/verify2FA/index';
import { protectedRoutes } from './utils/protectedRoutes';

const UNAUTHORIZED_ROUTE = '/sem-permissao';

// Renders a public page, redirecting authenticated users away from it.
function PublicRoute(props: {
  isTokenValid: boolean;
  redirectTo: string;
  children: ReactElement;
}) {
  return props.isTokenValid ? (
    <Navigate to={props.redirectTo} replace />
  ) : (
    props.children
  );
}

// Gates the private area behind a valid session.
function PrivateRoutes(props: { isTokenValid: boolean }) {
  return props.isTokenValid ? <Outlet /> : <Navigate to="/login" />;
}

export function Routes() {
  const { isTokenValid, isLoadingUser } = useAuthSession();
  const { isAdmin, canAccessSection, isLoadingSections } = useSectionAccess();

  const accessibleRoutes = useMemo(
    () => protectedRoutes.filter((route) => canAccessSection(route.key)),
    [canAccessSection]
  );

  const defaultRoute = useMemo(() => {
    if (isAdmin) return '/dashboard';
    const dashboard = accessibleRoutes.find(
      (route) => route.key === 'dashboard'
    );
    return dashboard?.path ?? accessibleRoutes[0]?.path ?? UNAUTHORIZED_ROUTE;
  }, [isAdmin, accessibleRoutes]);

  // Wait for the user (and, for non-admins, their sections) before deciding
  // which routes exist, otherwise we would briefly redirect to "unauthorized".
  const isCheckingAuth =
    isLoadingUser || (isTokenValid && !isAdmin && isLoadingSections);

  if (isCheckingAuth) {
    return <Spin tip="Carregando..." />;
  }

  return (
    <RRDRoutes>
      <Route path="/login" element={<SignIn />} />
      <Route
        path="/forgot-my-password"
        element={
          <PublicRoute isTokenValid={isTokenValid} redirectTo={defaultRoute}>
            <ForgotMyPassword />
          </PublicRoute>
        }
      />
      <Route
        path="/change-your-password/:token"
        element={
          <PublicRoute isTokenValid={isTokenValid} redirectTo={defaultRoute}>
            <ChangeYourPassword />
          </PublicRoute>
        }
      />
      <Route
        path="/confirm-financial-officers/:token/:table_id"
        element={
          <PublicRoute
            isTokenValid={isTokenValid}
            redirectTo="/confirm-financial-officers"
          >
            <ConfirmFinancialOfficers />
          </PublicRoute>
        }
      />
      <Route
        path="/verify-2fa"
        element={
          <PublicRoute isTokenValid={isTokenValid} redirectTo={defaultRoute}>
            <Verify2FA />
          </PublicRoute>
        }
      />

      <Route element={<PrivateRoutes isTokenValid={isTokenValid} />}>
        {/* Account security is available to any authenticated user. */}
        <Route path="/settings/security" element={<AccountSecurity />} />

        {accessibleRoutes.map((route) => (
          <Route key={route.path} path={route.path} element={route.element} />
        ))}

        <Route path="/" element={<Navigate to={defaultRoute} />} />
        <Route path={UNAUTHORIZED_ROUTE} element={<UnauthorizedPage />} />
      </Route>

      <Route
        path="*"
        element={<Navigate to={isTokenValid ? UNAUTHORIZED_ROUTE : '/login'} />}
      />
    </RRDRoutes>
  );
}
